/**
 * 难度定义（内容层）：参考东方 Project 的 4 档难度。
 *
 * 设计原则（调研结论，见 docs/01-design-research.md「难度分级」）：
 * 难度差异主要体现在【弹幕设计】——同一弹幕在不同难度下的弹数/层数/
 * 分支组件不同（东方 ECL 的难度开关、Danmakufu 的按难度分支惯例）。
 * 弹速只作辅助微调；敌人血量不随难度变化（与东方一致）。
 *
 * 字段（接线点）：
 *   density   弹幕弹量结构系数 —— patterns.js diffCount 按难度索引缩放各
 *             模式的弹数/旋臂/组件（唯一来源在此，patterns.js 直接引用）
 *   speed     敌弹速度系数（辅助微调）—— gameScene 注入 BulletField.speedMul
 *   spellTier 符卡 bonus 难度项 —— scoring.spellCardStart 第三参数
 *   scoreMul  分数系数 —— ScoreSystem.gain 统一缩放（高难度得分潜力更高；
 *             排行榜按难度分列，系数只影响同难度内的绝对分值）
 * node 可直接 import。
 */

/** @type {{id:string, name:string, cnName:string, density:number, speed:number,
 *   spellTier:number, scoreMul:number, desc:string}[]} */
export const DIFFICULTIES = [
  {
    id: 'easy', name: 'Easy', cnName: '简单',
    density: 0.9, speed: 0.9, spellTier: 0.5, scoreMul: 0.5,
    desc: '弹幕精简缓慢，符卡结构简化，适合初次接触弹幕游戏',
  },
  {
    id: 'normal', name: 'Normal', cnName: '普通',
    density: 1.9, speed: 1.05, spellTier: 1.0, scoreMul: 1.0,
    desc: '标准难度，关卡弹幕的完整设计形态',
  },
  {
    id: 'hard', name: 'Hard', cnName: '困难',
    density: 3.4, speed: 1.2, spellTier: 1.5, scoreMul: 1.5,
    desc: '弹量与层数大幅增加，符卡追加新组件',
  },
  {
    id: 'lunatic', name: 'Lunatic', cnName: '疯狂',
    density: 5.5, speed: 1.35, spellTier: 2.0, scoreMul: 2.0,
    desc: '弹幕地狱：全结构强化形态，献给真正的强者',
  },
];

/**
 * Extra 模式专用难度定义（不进难度选择界面）。
 * 弹幕结构与计分/符卡均按 Lunatic 档（diffIdx=3）。
 */
export const EXTRA_DIFF = {
  id: 'extra', name: 'Extra', cnName: '额外关', jaName: 'エクストラ', koName: '추가',
  // 弹幕结构按 Hard 档（各卡参数已按 Hard 基准手写迁移，输出不变）；
  // 计分/符卡按 Lunatic 档，弹速取 Hard/Lunatic 均值
  density: 3.4, speed: 1.28, spellTier: 2.0, scoreMul: 2.0,
  desc: '单关长 boss 战，献给通关后的强者',
};

/**
 * 按 id 取难度定义，未知 id 回落 Normal。
 * @param {string} [id]
 * @returns {object}
 */
export function getDifficulty(id) {
  if (id === 'extra') return EXTRA_DIFF;
  return DIFFICULTIES.find((d) => d.id === id) || DIFFICULTIES[1];
}

/**
 * 难度索引（0 Easy … 3 Lunatic），patterns.js 的难度分支以此为键。
 * @param {object} diff 难度定义
 * @returns {number} 未知定义回落 1（Normal）
 */
export function difficultyIndex(diff) {
  const i = DIFFICULTIES.indexOf(diff);
  return i >= 0 ? i : 1;
}
